/**
 * Política de caminho ICE — evita travamentos por flapping entre NICs.
 *
 * Sintoma clássico (dev Linux): Ethernet + Wi‑Fi + bridge Docker geram vários
 * host/srflx/prflx; o SFU alterna o pair selecionado a cada ~20–30s e o
 * estimador de banda (BWE) colapsa → vídeo 270p @ 3–7 fps.
 *
 * Mitigação: forçar TURN (`iceTransportPolicy: 'relay'`) quando detectado
 * multi-homing perigoso, ou via `?forceRelay=1` / localStorage.
 */

export const FORCE_RELAY_STORAGE_KEY = 'evolumeet.forceRelay';

/** Marcador de sessão: adaptação de rede usa grace maior após connect via TURN. */
export const FORCE_RELAY_SESSION_KEY = 'evolumeet.iceForceRelayActive';

export function markForceRelaySession(active: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    if (active) {
      window.sessionStorage.setItem(FORCE_RELAY_SESSION_KEY, '1');
    } else {
      window.sessionStorage.removeItem(FORCE_RELAY_SESSION_KEY);
    }
  } catch {
    /* ignore */
  }
}

export function isForceRelaySessionActive(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.sessionStorage.getItem(FORCE_RELAY_SESSION_KEY) === '1';
  } catch {
    return false;
  }
}

/** IPv4 em faixas típicas de bridge Docker / libvirt / k3s CNI. */
export function isDockerLikeIpv4(address: string): boolean {
  const m = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/.exec(address.trim());
  if (!m) return false;
  const [a, b] = [Number(m[1]), Number(m[2])];
  if (a === 172 && b >= 16 && b <= 31) return true; // 172.16/12
  if (a === 10 && b === 42) return true; // k3s flannel comum
  if (a === 10 && b === 244) return true;
  return false;
}

export function isPrivateIpv4(address: string): boolean {
  const m = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/.exec(address.trim());
  if (!m) return false;
  const [a, b] = [Number(m[1]), Number(m[2])];
  if (a === 10) return true;
  if (a === 192 && b === 168) return true;
  if (a === 172 && b >= 16 && b <= 31) return true;
  return false;
}

/** /24 do IPv4, para contar NICs distintas na mesma LAN. */
export function ipv4Slash24(address: string): string | null {
  const m = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/.exec(address.trim());
  if (!m) return null;
  return `${m[1]}.${m[2]}.${m[3]}.0/24`;
}

/**
 * Heurística: Docker bridge presente OU ≥2 hosts privados IPv4 distintos
 * (ex.: eth 192.168.1.25 + wifi 192.168.1.7) → risco alto de ICE flap.
 */
export function isHazardousIceHostSet(hostAddresses: string[]): boolean {
  const hosts = hostAddresses.map((a) => a.trim()).filter(Boolean);
  if (hosts.some(isDockerLikeIpv4)) return true;

  const privateIpv4 = hosts.filter(isPrivateIpv4);
  const unique = new Set(privateIpv4);
  if (unique.size >= 2) return true;

  // Mesmo /24 com 2+ IPs (eth+wifi no mesmo SSID/LAN) também flapa.
  const slash24Counts = new Map<string, Set<string>>();
  for (const ip of privateIpv4) {
    const net = ipv4Slash24(ip);
    if (!net) continue;
    if (!slash24Counts.has(net)) slash24Counts.set(net, new Set());
    slash24Counts.get(net)!.add(ip);
  }
  for (const ips of slash24Counts.values()) {
    if (ips.size >= 2) return true;
  }
  return false;
}

/**
 * Preferência explícita:
 * - `?forceRelay=1` / localStorage `'1'` → true
 * - `?forceRelay=0` / localStorage `'0'` → false (desliga auto)
 * - omitido → null (deixar auto-detect decidir)
 */
export function readForceRelayPreference(
  search: string,
  storageGet?: (key: string) => string | null,
): boolean | null {
  try {
    const params = new URLSearchParams(search.startsWith('?') ? search : `?${search}`);
    const q = params.get('forceRelay');
    if (q === '1' || q === 'true') return true;
    if (q === '0' || q === 'false') return false;
  } catch {
    /* ignore */
  }
  if (storageGet) {
    const stored = storageGet(FORCE_RELAY_STORAGE_KEY);
    if (stored === '1' || stored === 'true') return true;
    if (stored === '0' || stored === 'false') return false;
  }
  return null;
}

export function buildRtcConfigForIcePolicy(forceRelay: boolean): RTCConfiguration | undefined {
  if (!forceRelay) return undefined;
  // poolSize>0 acelera gather do TURN; bundle único evita múltiplos paths disputando.
  return {
    iceTransportPolicy: 'relay',
    bundlePolicy: 'max-bundle',
    rtcpMuxPolicy: 'require',
    iceCandidatePoolSize: 2,
  };
}

/**
 * Coleta endereços `host` locais via PeerConnection efêmero (sem STUN).
 * Timeout curto — falha = lista vazia (não força relay).
 */
export async function probeLocalHostAddresses(
  timeoutMs = 1500,
): Promise<string[]> {
  if (typeof RTCPeerConnection === 'undefined') return [];

  const pc = new RTCPeerConnection({ iceServers: [] });
  const hosts = new Set<string>();

  try {
    pc.createDataChannel('ice-probe');
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);

    await new Promise<void>((resolve) => {
      const done = () => resolve();
      const timer = window.setTimeout(done, timeoutMs);
      if (pc.iceGatheringState === 'complete') {
        window.clearTimeout(timer);
        done();
        return;
      }
      pc.onicegatheringstatechange = () => {
        if (pc.iceGatheringState === 'complete') {
          window.clearTimeout(timer);
          done();
        }
      };
    });

    const stats = await pc.getStats();
    stats.forEach((report) => {
      if (report.type !== 'local-candidate') return;
      const r = report as unknown as {
        candidateType?: string;
        address?: string;
        ip?: string;
      };
      if (r.candidateType !== 'host') return;
      const addr = r.address || r.ip;
      if (typeof addr === 'string' && addr.length > 0) hosts.add(addr);
    });
  } catch {
    /* ignore probe failures */
  } finally {
    try {
      pc.close();
    } catch {
      /* ignore */
    }
  }

  return [...hosts];
}

export async function resolveForceRelay(options: {
  search: string;
  storageGet?: (key: string) => string | null;
  probe?: () => Promise<string[]>;
}): Promise<{
  forceRelay: boolean;
  reason: 'explicit-on' | 'explicit-off' | 'auto-hazard' | 'default';
}> {
  const pref = readForceRelayPreference(options.search, options.storageGet);
  if (pref === true) return { forceRelay: true, reason: 'explicit-on' };
  if (pref === false) return { forceRelay: false, reason: 'explicit-off' };

  const probe = options.probe ?? probeLocalHostAddresses;
  try {
    const hosts = await probe();
    if (isHazardousIceHostSet(hosts)) {
      return { forceRelay: true, reason: 'auto-hazard' };
    }
  } catch {
    /* ignore */
  }
  return { forceRelay: false, reason: 'default' };
}
